import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { feedbackOpener, type FeedbackOpener } from '@/feedback/userback';
import { cn } from '@/lib/utils';

interface FeedbackMenuItemProps {
    /** Injectable for tests; defaults to the app-wide Userback opener. */
    feedback?: FeedbackOpener;
    /**
     * Called once the form is up (or gave up), so the parent can dismiss the
     * menu. Selecting the item deliberately does *not* close it on its own —
     * the menu has to stay put for the pending state to be visible.
     */
    onOpened?: () => void;
}

/**
 * "Send feedback" entry for the AppNav dropdown. Opens the Userback feedback
 * form (bring-your-own-button; see `feedback/userback.ts`).
 *
 * The widget is fetched on intent rather than on click: reaching for this item
 * (hover, or keyboard focus landing on it) warms Userback's remote script while
 * the user is still deciding, which is the whole several-second cost of a first
 * open. Reaching for the item is still the user asking for feedback, so this
 * keeps the click-is-the-opt-in stance in `feedback/userback.ts` — merely
 * opening the menu, which people do to navigate, loads nothing.
 *
 * A click that lands before the load finishes marks the item busy and holds the
 * menu open until the form appears, so it never looks like nothing happened.
 *
 * Renders nothing when no `VITE_USERBACK_TOKEN` is configured.
 */
export function FeedbackMenuItem({ feedback = feedbackOpener, onOpened }: FeedbackMenuItemProps) {
    const { t, i18n } = useTranslation();
    const [pending, setPending] = useState(false);
    const warmed = useRef<string | undefined>(undefined);
    const language = i18n.resolvedLanguage ?? i18n.language;

    if (!feedback.isAvailable()) return null;

    // Hover and focus both fire, and the pointer may cross the item repeatedly;
    // one warm-up per menu open per language is enough.
    const warm = () => {
        if (warmed.current === language) return;
        warmed.current = language;
        void feedback.preload(language);
    };

    return (
        <DropdownMenuItem
            aria-busy={pending}
            onPointerEnter={warm}
            onFocus={warm}
            className={cn(pending && 'opacity-60 animate-pulse')}
            onSelect={(event) => {
                // Keep the menu mounted; `onOpened` closes it once the form is up.
                event.preventDefault();
                if (pending) return;
                setPending(true);
                void feedback.open(language).finally(() => {
                    setPending(false);
                    onOpened?.();
                });
            }}
        >
            {t('nav.feedback')}
        </DropdownMenuItem>
    );
}
